// index.js force
import "./utils/domGuard";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./styles.css";
import CameraDashboard from "./camera/components/main/CameraDashboard";
import reportWebVitals from "./reportWebVitals";
import CreateCustomerExternalForm from "./createCustomer/components/CreateCustomerExternalForm";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import CustomerDashboard from "./customerDashboard/components/CustomerDashboard";
import CustomerDashboardStart from "./customerDashboard/components/CustomerDashboardStart";
import UrlPayments from "./urlPayments/components/UrlPayments.tsx";
import Login from "./login/components/Login.tsx";
import LisualSalesTeam from "./LandingPagePayments/components/LisualSalesTeam.tsx";
import FeedDaysCalculator from "./FeedDaysCalculator/components/FeedDaysCalculator.tsx";
import PaymentSelector from "./salesTeam/components/PaymentSelector.tsx";
import CreateGuestUser from "./urlUsers/components/CreateGuestUser.tsx";
import CreateResponsibleUser from "./urlUsers/components/CreateResponsibleUser.tsx";
import LoginDiscounts from "./login/components/LoginDiscounts.tsx";
import UserEditorDashboard from "./userEditorDashboard/components/UserEditorDashboard.tsx";
import QRScan from "./qrScan/components/QRScan.js";
import CamaraSetupWizard from "./camaraSetupWizard/components/CamaraSetupWizard.tsx";
import GuestDashboard from "./guestDashboard/components/GuestDashboard.js";
import AdminDashboard from "./adminDashboard/components/AdminDashboard.jsx";
import SettingNotification from "./notifications/components/SettingNotification.tsx";
import DashboardAdminLayout from "./adminDashboard/components/DashboardAdminLayout.jsx";
import StockCamTable from "./stockCams/components/StockCamTable.tsx";
import Cobranzas from "./adminDashboard/components/Cobranzas.jsx";
import SalesCodes from "./adminDashboard/components/SalesCodes.tsx";
import StockChipTable from "./stockChips/components/StockChipsTable.tsx";
import PendingCamaras from "./pendingCamaras/components/PendingCamaras.tsx";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Monitor from "./monitor/components/Monitor.tsx";
import LogsDashboard from "./adminDashboard/components/LogsDashboard.jsx";
import LicenciasDashboard from "./adminDashboard/components/LicenciasDashboard.jsx";
import PublicIframePage from "./publicIframe/pages/PublicIframePage";
import PublicCompareIframePage from "./publicIframe/pages/PublicCompareIframePage";
import PublicMondayIframePage from "./publicIframe/pages/PublicMondayIframePage";
import PrivacyPolicy from "./legal/components/PrivacyPolicy.tsx";

import { setAuthRedirect, isTokenExpired, logoutAndRedirect, getTokenRemainingTime } from "./utils/authRedirect";

const GlobalAuthHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setAuthRedirect(navigate);

    // Lógica de monitoreo de sesión
    const checkSession = () => {
      const token = localStorage.getItem("authLisualToken");
      if (!token) return;

      const remainingTime = getTokenRemainingTime(token);

      // Si queda menos de 5 minutos (300 segundos) y más de 0
      if (remainingTime > 0 && remainingTime < 300) {
        toast.warn("Tu sesión está por expirar en pocos minutos. Por favor, asegúrate de guardar tus cambios.", {
          toastId: 'session-warning', // ID único para que no se repita
          autoClose: 10000,
          position: "top-center"
        });
      }

      // Si ya expiró
      if (remainingTime <= 0) {
        logoutAndRedirect();
      }
    };

    // Revisar cada 1 minuto
    const interval = setInterval(checkSession, 60000);
    checkSession(); // Revisar al cargar

    return () => clearInterval(interval);
  }, [navigate]);

  return <Outlet />;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("authLisualToken");

  if (!token || isTokenExpired(token)) {
    logoutAndRedirect();
    return <Navigate to="/" replace />;
  }
  return children;
};

const router = createBrowserRouter([
  {
    element: <GlobalAuthHandler />,
    children: [
      {
        path: "/",
        element: <Login />
      },
      {
        path: "/testscrollnahuel",
        element: <LoginDiscounts />,
      },
      {
        path: "/__preview-downloads",
        element: (() => {
          const DownloadsPreview = require("./camera/components/downloads/Downloads").default;
          return (
            <div className="min-h-screen bg-gray-100 p-6">
              <div className="max-w-5xl mx-auto">
                <DownloadsPreview user={{ Name: "Obra Demo", feedID: "demo123", customerId: "cust1" }} />
              </div>
            </div>
          );
        })(),
      },
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: la pantalla de empresa con la campanita de notificaciones (?customerId=&logo=0|1&novedad=0|1)
        path: "/__preview-campanita",
        element: (() => {
          const CampanitaPreview = require("./dev/CampanitaPreview").default;
          return <CampanitaPreview />;
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Marketing › Comunidad sin loguearse. Los datos salen de public/__dev/comunidad.json (no se commitea);
        // contacto y visibilidad se simulan (no mandan mails ni cambian nada).
        path: "/__preview-comunidad",
        element: (() => {
          const Comunidad = require("./camera/components/comunidad/Comunidad").default;
          const { comunidadApi } = require("./camera/components/comunidad/comunidad.api");
          const resp = (config, data) => ({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });
          comunidadApi.defaults.adapter = async (config) => {
            if ((config.method || "get").toLowerCase() === "get") return resp(config, await fetch("/__dev/comunidad.json").then((r) => r.json()));
            await new Promise((r) => setTimeout(r, 600));
            return resp(config, { ok: true });
          };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <div style={{ maxWidth: 1024, margin: "0 auto" }}><Comunidad /></div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: los períodos del modo Básico del Editor de Video con una fecha de primera foto (?firstDate=YYYY-MM-DD)
        path: "/__preview-editor-basico",
        element: (() => {
          const BasicPresetPicker = require("./videoEditor/components/BasicPresetPicker").default;
          const { EDITOR_PRESETS, isPresetAvailable } = require("./videoEditor/utils/editorPresets");
          const firstDate = new URLSearchParams(window.location.search).get("firstDate");
          const availability = { firstDate };
          EDITOR_PRESETS.forEach((p) => { availability[p.id] = isPresetAvailable(p.id, firstDate, new Date()); });
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <div style={{ maxWidth: 760, margin: "0 auto", background: "#fff", borderRadius: 12, padding: 16 }}>
                <BasicPresetPicker selectedId="LAST_MONTH" availability={availability} submitting={false} onSelect={() => {}} />
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: la tarjeta del Inicio de una cámara (Antes y Ahora / Rayos X / Timelapse) sin pasar por /dashboard (?customerId=&identifier=&name=&feedId=&orientation=)
        path: "/__preview-inicio",
        element: (() => {
          const InicioMediaCard = require("./camera/components/firstLastImageCompare/InicioMediaCard").default;
          const PreviewNotification = require("./notifications/components/Notification.tsx").default;
          const q = new URLSearchParams(window.location.search);
          require("./dev/previewInicioMock").default(q.get("feedId"));
          const userdata = { feedID: q.get("feedId"), Name: q.get("name"), customerId: q.get("customerId"), identifier: q.get("identifier"), orientation: q.get("orientation") || undefined };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <div style={{ maxWidth: 960, margin: "0 auto" }}>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Vista previa local · {q.get("name") || q.get("identifier")}</p>
                <div className="space-y-3">
                <PreviewNotification />
                <InicioMediaCard userdata={userdata} onGoToFirstImage={() => {}} onOpenEditor={() => {}} />
                </div>
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el saludo de la pantalla de empresa con el nivel de fidelidad (?name=&n=4&zona=amba|remoto)
        path: "/__preview-fidelidad",
        element: (() => {
          require("./customerDashboard/style/CustomerDashboard.css");
          const NivelFidelidad = require("./customerDashboard/components/NivelFidelidad.jsx").default;
          const q = new URLSearchParams(window.location.search);
          const n = parseInt(q.get("n") || "4", 10);
          const coordinates = q.get("zona") === "remoto" ? { lat: -31.42, lng: -64.19 } : { lat: -34.6, lng: -58.43 };
          const camaras = [...Array(n).fill(0).map(() => ({ status: "Aprobado", coordinates })), { status: "Terminado", coordinates }];
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 12 }}>
              <div className="dashboard-container">
                <div className="cd-bienvenida">
                  <h1><strong>Bienvenido/a: </strong>{q.get("name") || "Nico Dufre"}</h1>
                  <NivelFidelidad camaras={camaras} />
                </div>
                <div style={{ background: "#fffcf0", border: "2px solid #fbbf24", borderRadius: 14, padding: "8px 14px", fontSize: 13 }}>
                  <strong>NOVEDADES OCTUBRE 2026</strong> · Conocé lo último que implementamos
                </div>
                <div className="project-grid" style={{ marginTop: 24 }}>
                  {Array(n).fill(0).map((_, i) => <div key={i} className="project-card" style={{ height: 200, background: "#cbd5e1" }} />)}
                </div>
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: ver el Dashboard IA de una cámara sin loguearse (?customerId=...&identifier=P_xxx&name=...)
        path: "/__preview-dashboard-ia",
        element: (() => {
          const IADashboard = require("./camera/components/IADashboard/IADashboard").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <IADashboard customerId={q.get("customerId")} camIdentifier={q.get("identifier")} camName={q.get("name")} feedId={q.get("feedId")} onEditZonas={() => { window.location.href = `/__preview-zonas${window.location.search}`; }} coords={q.get("lat") ? { lat: q.get("lat"), lng: q.get("lng") } : null} />
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Zonas de detección de una cámara sin loguearse (?customerId=...&feedId=...&identifier=P_xxx&name=...)
        path: "/__preview-zonas",
        element: (() => {
          const ZonasDeteccion = require("./camera/components/IADashboard/ZonasDeteccion").default;
          const q = new URLSearchParams(window.location.search);
          const dash = `/__preview-dashboard-ia${window.location.search}`;
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <ToastContainer position="top-center" autoClose={4000} />
              <ZonasDeteccion customerId={q.get("customerId")} camIdentifier={q.get("identifier")} camName={q.get("name")} feedId={q.get("feedId")} onVerDashboard={() => { window.location.href = dash; }} />
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el widget de ayuda reconociendo a un usuario logueado (?name=&phone=&feedId=&feedName=)
        path: "/__preview-widget",
        element: (() => {
          const WidgetPreview = require("./dev/WidgetPreview").default;
          return <WidgetPreview />;
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Obra › Clima de una cámara sin loguearse (?feedId=...&identifier=P_xxx&name=...&customerId=...&empresa=...&logo=...&lat=&lng=&clima=<pestaña>)
        // Con REACT_APP_OBRA_API_URL=http://localhost:8094 la ubicación se lee/guarda en widget-dev (JSON local), nunca en producción.
        path: "/__preview-clima",
        element: (() => {
          const WeatherComponent = require("./camera/components/weather/WeatherComponent.js").default;
          const q = new URLSearchParams(window.location.search);
          const userdata = { feedID: q.get("feedId"), identifier: q.get("identifier"), Name: q.get("name"), customerId: q.get("customerId"), coordinates: q.get("lat") ? { lat: q.get("lat"), lng: q.get("lng") } : undefined };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 12 }}>
              <div style={{ maxWidth: 1180, margin: "0 auto" }}>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Vista previa local · Obra › Clima · {q.get("name") || q.get("identifier")}</p>
                <ToastContainer position="top-center" autoClose={3500} />
                <WeatherComponent user={userdata} empresa={{ customerName: q.get("empresa") || "", logo: q.get("logo") || "" }} />
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el Calendario de obra de una cámara sin loguearse (?customerId=...&identifier=P_xxx&name=...)
        path: "/__preview-calendario",
        element: (() => {
          const CalendarioObra = require("./camera/components/IADashboard/CalendarioObra").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <CalendarioObra customerId={q.get("customerId")} cam={q.get("identifier")} name={q.get("name")} feedId={q.get("feedId")} coords={q.get("lat") ? { lat: q.get("lat"), lng: q.get("lng") } : null} />
            </div>
          );
        })(),
      }] : []),
      // Sólo local: Inteligencia › Maquinaria / Entradas y salidas / Materiales / Acopio sin loguearse
      // (?customerId=...&identifier=P_xxx&name=...&feedId=...)
      ...(process.env.NODE_ENV === "development" ? [
        ["/__preview-buscador", "Maquinaria"], // Buscador de obra (ex Maquinaria)
        ["/__preview-maquinaria", "Maquinaria"],
        ["/__preview-entradas", "EntradasSalidas"],
        ["/__preview-materiales", "Materiales"],
        ["/__preview-acopio", "Acopio"],
      ].map(([path, mod]) => ({
        path,
        element: (() => {
          const Pagina = require(`./camera/components/IADashboard/inteligencia/${mod}`).default;
          const ComboIAGate = require("./camera/components/comboIA/ComboIAGate").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <div style={{ maxWidth: 1024, margin: "0 auto" }}>
                {/* ?combo=activo|prueba|no fuerza el estado del combo IA de obra (sin backend) */}
                <ComboIAGate feedID={q.get("feedId")} pagina={mod === "EntradasSalidas" ? "Entradas y salidas" : mod === "Maquinaria" ? "Buscador de obra" : mod}>
                  <Pagina customerId={q.get("customerId")} cam={q.get("identifier")} name={q.get("name")} feedId={q.get("feedId")} />
                </ComboIAGate>
              </div>
            </div>
          );
        })(),
      })) : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el menú de arriba de una cámara (ítems e insignias PRO reales), sin loguearse. ?ia=0 = cámara sin IA.
        path: "/__preview-menu",
        element: (() => {
          const TopNavbar = require("./camera/components/topNavbar/TopNavbar").default;
          const { itemsInteligencia } = require("./camera/components/main/CameraDashboard");
          const { ITEMS_PRO, ITEMS_PRO_PLUS } = require("./camera/components/comboIA/comboIA");
          const L = require("lucide-react");
          const conIa = new URLSearchParams(window.location.search).get("ia") !== "0";
          const iconos = { "Dashboard IA": <L.Sparkles size={16} />, "Buscador de obra": <L.SearchIcon size={16} />, "Entradas y salidas": <L.ArrowLeftRight size={16} />, Materiales: <L.PackageOpen size={16} />, Acopio: <L.Boxes size={16} />, "Seguridad e Higiene": <L.HardHat size={16} />, "Zonas de detección": <L.Crop size={16} />, Alertas: <L.Bell size={16} />, "Reporte PDF": <L.FileText size={16} /> };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
              <TopNavbar menuItems={[{ name: "Inicio" }]} groupedTabs={[{ label: "Obra", items: ["Datos de la obra"] }, { label: "Inteligencia", items: itemsInteligencia(conIa) }, { label: "Marketing", items: ["Redes Sociales"] }, { label: "Soporte", items: ["Ayuda"] }]} badgeMap={{ ...Object.fromEntries(ITEMS_PRO.map((n) => [n, "PRO"])), ...Object.fromEntries(ITEMS_PRO_PLUS.map((n) => [n, "PRO+"])) }} iconMap={iconos} activeTab="Seguridad e Higiene" setActiveTab={() => {}} />
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el menú Obra (sin Primera Imagen ni Descargar imágenes) y Archivo Fotográfico con sus secciones
        // (?feedId=&identifier=&customerId=&name=&seccion=fotos|primera|descargar). Sin sesión, las fotos pueden no cargar.
        path: "/__preview-archivo",
        element: (() => {
          const TopNavbar = require("./camera/components/topNavbar/TopNavbar").default;
          const { itemsObra, SECCION_DE_PESTANA } = require("./camera/components/main/CameraDashboard");
          const ArchivoFotografico = require("./camera/components/archivoFotografico/ArchivoFotografico").default;
          const q = new URLSearchParams(window.location.search);
          const user = { feedID: q.get("feedId"), identifier: q.get("identifier"), customerId: q.get("customerId"), Name: q.get("name") };
          function Vista() {
            const [tab, setTab] = React.useState("Archivo Fotografico");
            const [seccion, setSeccion] = React.useState(q.get("seccion") || "fotos");
            const [fecha, setFecha] = React.useState(new Date());
            const ir = (t) => { if (SECCION_DE_PESTANA[t]) { setTab("Archivo Fotografico"); setSeccion(SECCION_DE_PESTANA[t]); } else { setTab(t); setSeccion("fotos"); } };
            return (
              <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
                <TopNavbar menuItems={[{ name: "Inicio" }]} groupedTabs={[{ label: "Obra", items: itemsObra(true, true) }, { label: "Marketing", items: ["Redes Sociales"] }]} badgeMap={{}} activeTab={tab} setActiveTab={ir} />
                <div style={{ maxWidth: 1024, margin: "0 auto", padding: "84px 16px 16px" }}>
                  {tab === "Archivo Fotografico" ? <ArchivoFotografico user={user} seccion={seccion} onSeccion={setSeccion} selectedDate={fecha} onDateSelect={setFecha} /> : <p style={{ color: "#6b7280" }}>{tab} (vista previa)</p>}
                  <p style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>Links viejos: <button type="button" onClick={() => ir("Primera Imagen")} style={{ color: "#1d4ed8" }}>"Primera Imagen"</button> · <button type="button" onClick={() => ir("Descargar imágenes")} style={{ color: "#1d4ed8" }}>"Descargar imágenes"</button></p>
                </div>
              </div>
            );
          }
          return <Vista />;
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Jairo asigna a qué cámara va cada web dudosa (guarda en _repos-lisual/asignaciones_webs.json).
        path: "/__asignar-webs",
        element: (() => { const AsignarWebs = require("./dev/AsignarWebs").default; return <AsignarWebs />; })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: las pestañas Recorrido 360° y Página web con el menú de arriba, sin loguearse (?feedId=...&name=...&empresa=...&tab=web).
        // Una cámara con 360 cargado lo muestra; cualquier otra ve "Contratar servicio". &demo=secciones = varias obras y fechas.
        path: "/__preview-360",
        element: (() => {
          const PreviewWebs = require("./recorrido360/PreviewWebs").default;
          return <><ToastContainer position="bottom-right" autoClose={3500} /><PreviewWebs /></>;
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Inteligencia › Seguridad e Higiene sin loguearse (?customerId=...&identifier=P_xxx&name=...&feedId=...&combo=activo|prueba|no&seguridad=activo|prueba|no)
        // Con REACT_APP_SEGURIDAD_API_URL=http://localhost:8097 la config y los reportes de personas salen de `npm run seguridad:dev`.
        path: "/__preview-seguridad",
        element: (() => {
          const SeguridadHigiene = require("./camera/components/Seguridad/SeguridadHigiene").default;
          const SeguridadGate = require("./camera/components/comboIA/SeguridadGate").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 12 }}>
              <div style={{ maxWidth: 1280, margin: "0 auto" }}>
                <SeguridadGate feedID={q.get("feedId")}>
                  <SeguridadHigiene customerId={q.get("customerId")} cam={q.get("identifier")} name={q.get("name")} feedId={q.get("feedId")} onIrA={(t) => alert(`Ir a ${t}`)} />
                </SeguridadGate>
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Obra › Datos de la obra y Marketing › Página web sin loguearse, con el mismo menú de pestañas
        // (?customerId=...&feedId=...&identifier=P_xxx&name=...&tab=datos|web&lat=&lng=&empresa=&logo=)
        path: "/__preview-datos-obra",
        element: (() => {
          const PreviewDatosObra = require("./datosObra/PreviewDatosObra").default;
          return (
            <>
              <ToastContainer position="bottom-right" autoClose={3500} />
              <PreviewDatosObra />
            </>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Marketing › Redes sociales sin loguearse (?customerId=...&feedId=...&obra=...&empresa=...&tab=plantillas)
        // Con REACT_APP_REDES_API_URL=http://localhost:8099 habla con el backend SIMULADO (no publica en ninguna red real).
        path: "/__preview-redes",
        element: (() => {
          const RedesSociales = require("./redes/RedesSociales").default;
          const SocialStudio = require("./socialStudio/SocialStudio").default;
          const q = new URLSearchParams(window.location.search);
          const user = { customerId: q.get("customerId") || "gsg11022026", feedID: q.get("feedId") || "fexfwx0dwzhy", Name: q.get("obra") || "Lagos del Golf 468" };
          const customerInfo = { name: q.get("empresa") || "GSG Arquitectos", logo: q.get("logo") || "" };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 12 }}>
              <ToastContainer position="bottom-right" autoClose={3500} />
              <div style={{ maxWidth: 1180, margin: "0 auto" }}>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Vista previa local · Marketing › Redes sociales · {user.Name}</p>
                <RedesSociales user={user} customerInfo={customerInfo} pestanaInicial={q.get("tab") || undefined}
                  vidriera={(onProgramar) => (localStorage.getItem("authLisualToken")
                    ? <SocialStudio user={user} customerInfo={customerInfo} identifier={q.get("identifier") || ""} onProgramar={onProgramar} />
                    : <div className="bg-white border border-gray-200 rounded-2xl p-5 text-sm text-gray-600">La vidriera de plantillas usa las fotos reales de la cámara: se ve entrando con tu usuario (localhost:3027 › tu obra › Marketing › Redes Sociales › Plantillas).</div>)} />
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Marketing › Tu competencia sin loguearse (?customerId=...). Con REACT_APP_COMPETENCIA_API_URL=http://localhost:8095
        // habla con el devServer del backend (npm run competencia:dev: Instagram real si tiene COMPETENCIA_IG_TOKEN).
        path: "/__preview-competencia",
        element: (() => {
          const TuCompetencia = require("./competencia/TuCompetencia").default;
          const q = new URLSearchParams(window.location.search);
          const user = { customerId: q.get("customerId") || "gsg11022026", Name: q.get("obra") || "Lagos del Golf 468" };
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 12 }}>
              <ToastContainer position="bottom-right" autoClose={3500} />
              <div style={{ maxWidth: 1180, margin: "0 auto" }}>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Vista previa local · Marketing › Tu competencia</p>
                <TuCompetencia user={user} isAdmin={q.get("admin") === "1"} />
              </div>
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: el Newsletter (beta) de un cliente sin loguearse (?customerId=...&feedId=...&obra=...&empresa=...&logo=...)
        path: "/__preview-newsletter",
        element: (() => {
          const Newsletter = require("./newsletter/Newsletter").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <ToastContainer position="bottom-right" />
              <Newsletter user={{ customerId: q.get("customerId"), feedID: q.get("feedId"), obraName: q.get("obra") }} customerInfo={{ name: q.get("empresa"), logo: q.get("logo"), mainUserEmail: q.get("email") }} />
            </div>
          );
        })(),
      }] : []),
      ...(process.env.NODE_ENV === "development" ? [{
        // Sólo local: Inteligencia > Alertas de una cámara sin loguearse (?customerId=...&feedId=...&identifier=P_xxx&name=...&phone=549...)
        path: "/__preview-alertas",
        element: (() => {
          const Alertas = require("./camera/components/alertas/Alertas").default;
          const q = new URLSearchParams(window.location.search);
          return (
            <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 16 }}>
              <ToastContainer position="bottom-right" />
              <div style={{ maxWidth: 880, margin: "0 auto" }}>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">{q.get("name") || "Obra"} · Alertas</h2>
                <Alertas feedID={q.get("feedId")} telefonoSugerido={q.get("phone")} />
              </div>
            </div>
          );
        })(),
      }] : []),
      {
        path: "dashboard/:identifier",
        element: (
            <ProtectedRoute>
              <CameraDashboard />
            </ProtectedRoute>
        ),
      },
      {
        path: "adminDashboard",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <AdminDashboard />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "stockCams",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <StockCamTable />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "stockChips",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <StockChipTable />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "salesCodes",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <SalesCodes />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "adminSales",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <PaymentSelector embedded />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "adminSales/createCustomer",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <UrlPayments embedded />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "adminSales/createStripeLink",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <LisualSalesTeam embedded />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "adminMonitor",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <Monitor />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "cobranzas/:section?",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <Cobranzas />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "pendingProjects",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <PendingCamaras />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "logs",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <LogsDashboard />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "licencias",
        element: (
            <ProtectedRoute>
              <DashboardAdminLayout>
                <LicenciasDashboard />
              </DashboardAdminLayout>
            </ProtectedRoute>
        ),
      },
      {
        path: "/customerRegister",
        element:
            <ProtectedRoute>
              <CreateCustomerExternalForm />
            </ProtectedRoute>
      },
      {
        path: "/customerDashboard/:customerId",
        element: (
            <ProtectedRoute>
              <CustomerDashboard />
            </ProtectedRoute>
        ),
      },
      {
        path: "/customerDashboard/:customerId/:identifier",
        element: (
            <ProtectedRoute>
              <CustomerDashboardStart />
            </ProtectedRoute>
        ),
      },
      {
        path: "/guestDashboard/:customerId",
        element: (
            <ProtectedRoute>
              <GuestDashboard />
            </ProtectedRoute>
        ),
      },
      {
        path: "/SettingNotifications/",
        element: (
            <ProtectedRoute>
              <SettingNotification />
            </ProtectedRoute>
        ),
      },
      {
        path: "/urlPayment/laks1920idjoals0i20pdkaosidj09ioijldaksdj01209iaopsdkdaklsd0912djaois_100",
        element:
            <ProtectedRoute>
              <UrlPayments />
            </ProtectedRoute>
      },
      {
        path:"/lisualSales/createCustomer",
        element:
            <ProtectedRoute>
              <UrlPayments />
            </ProtectedRoute>
      },
      {
        path: "/lisualSales/createStripeLink",
        element:
            <ProtectedRoute>
              <LisualSalesTeam />
            </ProtectedRoute>
      },
      {
        path: "/feedDaysCalculator",
        element:
            <ProtectedRoute>
              <FeedDaysCalculator />
            </ProtectedRoute>
      },
      {
        path: "/sales",
        element:
            <ProtectedRoute>
              <PaymentSelector />
            </ProtectedRoute>
      },
      {
        path: "/createUniqueResponsibleUserUsingURL/:customerId/j3d1e54ujnn178102nc332f38921dg",
        element: <CreateResponsibleUser />,
      },
      {
        path: "/createGuestUserUsingURL/:customerId/:identifier/kbvalcba0jb184b12annmabdo2",
        element: <CreateGuestUser />,
      },
      {
        path: "/customersAndCamListDashboard/",
        element: (
            <ProtectedRoute>
              <UserEditorDashboard />
            </ProtectedRoute>
        ),
      },
      {
        path: "/camaraSetupWizard/:customerId/:identifier",
        element: (
            <ProtectedRoute>
              <CamaraSetupWizard />
            </ProtectedRoute>
        ),
      },
      {
        path: "/monitor",
        element:
            <ProtectedRoute>
                <Monitor />
            </ProtectedRoute>


      },
      {
        path: "/public-iframe",
        element: <PublicIframePage />
      },
      {
        path: "/public-compare",
        element: <PublicCompareIframePage />
      },
      {
        path: "/public-mondays",
        element: <PublicMondayIframePage />
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicy />
      },
    ]
  }
]);

const root = ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
);

reportWebVitals();